/**
 * iter131 — MessageThread: reusable WhatsApp-style chat thread renderer.
 *
 * Used by both the dedicated ConversationThreadScreen (bottom-nav) and the
 * Voucher popup's GuestWhatsAppJourney section. Read-only — no compose
 * field (the App does not send messages, only mirrors the Admin Panel).
 *
 * Props
 * ─────
 *   messages           Array<Message>  — chronological (oldest → newest)
 *   guestName          string
 *   loading            boolean
 *   emptyLabel         string (optional)
 */
import React, { useEffect, useRef } from 'react';
import { CheckCheck, Image as ImageIcon, FileText, Video, Mic, MapPin } from 'lucide-react';

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return ''; }
};

const formatDateLabel = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
    const isSame = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    if (isSame(d, today)) return 'Today';
    if (isSame(d, yesterday)) return 'Yesterday';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

const bodyOf = (m) => {
  if (m.button_value) return `▸ ${m.button_value}`;
  if (m.message_text && m.message_text.trim()) return m.message_text;
  if (m.template_name) return `[Template: ${m.template_name}]`;
  return '';
};

const MediaBadge = ({ msg }) => {
  const t = msg.message_type;
  if (!msg.media_url && !['image', 'document', 'video', 'audio', 'location', 'sticker'].includes(t)) return null;
  const Icon = t === 'image' ? ImageIcon : t === 'document' ? FileText : t === 'video' ? Video : t === 'audio' ? Mic : t === 'location' ? MapPin : ImageIcon;
  return (
    <a
      href={msg.media_url || '#'}
      target={msg.media_url ? '_blank' : undefined}
      rel="noreferrer"
      className="inline-flex items-center gap-1 mt-1 text-xs text-blue-600 hover:underline"
      data-testid={`msg-media-${t}`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="uppercase">{t}</span>
    </a>
  );
};

const MessageBubble = ({ msg }) => {
  const isOutbound = msg.direction === 'outbound';
  const isTemplate = msg.message_type === 'template';
  const align = isOutbound ? 'justify-end' : 'justify-start';
  const bubbleClasses = isOutbound
    ? 'bg-emerald-50 border border-emerald-200 text-gray-800 rounded-tr-sm'
    : 'bg-white border border-gray-200 text-gray-900 rounded-tl-sm';

  return (
    <div className={`flex ${align}`} data-testid={`msg-${msg.direction}`}>
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 shadow-sm ${bubbleClasses}`}>
        {isOutbound && isTemplate && msg.template_name && (
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1" data-testid="msg-template-tag">
            Template: {msg.template_name}
          </div>
        )}
        <div className="whitespace-pre-wrap text-sm leading-relaxed" data-testid="msg-body">
          {bodyOf(msg)}
        </div>
        <MediaBadge msg={msg} />
        <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-gray-500">
          <span data-testid="msg-time">{formatTime(msg.occurred_at)}</span>
          {isOutbound && msg.delivery_status && (
            <span data-testid="msg-delivery-status" className={msg.delivery_status === 'read' ? 'text-blue-500' : ''}>
              <CheckCheck className="w-3 h-3 inline" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const DaySeparator = ({ dateIso }) => (
  <div className="flex items-center justify-center my-3" data-testid="msg-day-separator">
    <span className="text-[11px] px-3 py-1 bg-gray-100 text-gray-600 rounded-full font-medium">
      {formatDateLabel(dateIso)}
    </span>
  </div>
);

const MessageThread = ({ messages = [], loading = false, emptyLabel = 'No messages yet.' }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  if (loading) {
    return (
      <div className="p-4 space-y-2" data-testid="msg-thread-loading">
        {[1, 2, 3].map(i => (
          <div key={i} className={`h-12 rounded-2xl bg-gray-100 animate-pulse ${i % 2 ? 'w-3/4' : 'w-2/3 ml-auto'}`} />
        ))}
      </div>
    );
  }

  if (!messages.length) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-gray-500 text-sm" data-testid="msg-thread-empty">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-2">
          <FileText className="w-5 h-5 text-gray-400" />
        </div>
        {emptyLabel}
      </div>
    );
  }

  // Group messages by day for date separators
  const rendered = [];
  let lastDay = null;
  messages.forEach((m) => {
    const dayKey = (m.occurred_at || '').slice(0, 10);
    if (dayKey && dayKey !== lastDay) {
      rendered.push(<DaySeparator key={`day-${dayKey}-${m.id || m.provider_message_id}`} dateIso={m.occurred_at} />);
      lastDay = dayKey;
    }
    rendered.push(<MessageBubble key={m.id || m.provider_message_id || `${m.occurred_at}-${m.direction}`} msg={m} />);
  });

  return (
    <div
      ref={scrollRef}
      className="flex flex-col gap-2 p-3 bg-gradient-to-b from-gray-50 to-white"
      data-testid="msg-thread"
    >
      {rendered}
    </div>
  );
};

export default MessageThread;
